import { Component, Input, EventEmitter, Output, OnInit } from '@angular/core';
import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ClrDatepickerModule, ClrIconModule, ClrInputModule, ClrModalModule, ClrSelectModule } from '@clr/angular';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import {
  ClarityIcons,
  checkCircleIcon,
  fileIcon,
  plusCircleIcon,
  timesCircleIcon,
  uploadIcon,
  truckIcon
} from '@cds/core/icon';

import { Warehouse } from '../../../../_shared/interfaces';
import { getTenantStorage } from '../../../../_shared/utils/tenant-context';
import { filesBaseUrl } from '../../../../_shared/utils/rest-url';
import {
  AlertService,
  FileStoreService,
  IAlertType,
  WarehouseStoreService,
} from '../../../../_shared/services';

@Component({
  selector: 'app-order-details-warehouse-position',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    NgClass,
    DatePipe,
    RouterLink,
    ReactiveFormsModule,
    ClrSelectModule,
    ClrDatepickerModule,
    ClrInputModule,
    ClrIconModule,
    NgxExtendedPdfViewerModule,
    ClrModalModule,
  ],
  templateUrl: './order-details-warehouse-position.component.html',
  styleUrl: './order-details-warehouse-position.component.sass',
})
export class OrderDetailsWarehousePositionComponent implements OnInit {
  warehouse: Warehouse[] = [];
  warehouseForm: FormGroup = new FormGroup({});
  loading = true;
  showPdf = false;
  modalUrl = '';
  modalContent = '';
  editPos = 0;
  selectedFile: File | null = null;
  uploading = false;

  private filesDirectory = filesBaseUrl();

  @Input() projectId = '';
  @Input() jobId = 0;
  @Input() orderId = 0;
  @Input() positionId = 0;
  @Input() partId = 0;
  @Input() technicalDrawing = '';
  @Output() updateParent = new EventEmitter<void>();

  constructor(
    private waress: WarehouseStoreService,
    private fileService: FileStoreService,
    private fb: FormBuilder,
    private alertDispatcher: AlertService,
  ) {
    ClarityIcons.addIcons(fileIcon, plusCircleIcon, checkCircleIcon, timesCircleIcon, uploadIcon, truckIcon);
  }

  ngOnInit(): void {
    this.loadPositionsWarehouse();
    this.initWarehouseForm();
  }

  loadPositionsWarehouse(): void {
    this.waress.getPositionsWarehouse(this.projectId, this.jobId, this.orderId, this.positionId, this.partId).subscribe({
      next: data => { this.warehouse = data?.object ?? []; },
      complete: () => { this.loading = false; },
      error: () => { this.loading = false; },
    });
  }

  private initWarehouseForm(): void {
    this.warehouseForm = this.fb.group({
      company_id: 0,
      date: '',
      file_id: '',
      job_id: this.jobId,
      number_of_pieces: 0,
      order_id: this.orderId,
      part: this.partId,
      position: this.positionId,
      project_id: this.projectId,
      staff_id: parseInt(getTenantStorage('staff_id') ?? getTenantStorage('user_id') ?? '0', 10),
      technical_drawing: this.technicalDrawing,
      movement_type: 'incoming',
      serial_nr: '',
      heat_nr: '',
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  private normalizeDate(value: any): string {
    if (!value) return '';
    if (value instanceof Date) {
      const d = value as Date;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (typeof value === 'string') {
      // Clarity datepicker sends MM/DD/YYYY in en-US locale
      const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
    }
    return String(value);
  }

  saveWarehouseEntry(): void {
    const raw = { ...this.warehouseForm.value };
    raw.date = this.normalizeDate(raw.date);

    this.waress.addToWarehouse(raw).subscribe({
      next: () => {
        this.editPos = 0;
        this.selectedFile = null;
        this.loadPositionsWarehouse();
        this.updateParent.emit();
        this.alertDispatcher.show(
          { message: `Lieferung zur Position ${this.positionId} gespeichert.`, clrAlertType: IAlertType.SUCCESS },
          3000
        );
        this.initWarehouseForm();
      },
      error: (err: any) => {
        this.uploading = false;
        const msg = err?.error?.message ?? 'Lieferung konnte nicht gespeichert werden.';
        this.alertDispatcher.show({ message: msg, clrAlertType: IAlertType.DANGER }, 5000);
      },
    });
  }

  addToWarehouse(): void {
    if (!this.selectedFile) {
      this.saveWarehouseEntry();
      return;
    }

    this.uploading = true;
    const description = `Wareneingangsschein ${this.projectId}.${String(this.jobId).padStart(4, '0')}.${this.orderId}`;
    this.fileService.sendFile(
      this.selectedFile, 75, this.orderId, this.projectId, this.jobId, this.technicalDrawing, description
    ).subscribe({
      next: (res: any) => {
        const fileId: string = res?.data?.id ?? '';
        if (fileId) {
          this.warehouseForm.patchValue({ file_id: fileId });
        }
        this.uploading = false;
        this.saveWarehouseEntry();
      },
      error: () => {
        this.uploading = false;
        this.alertDispatcher.show(
          { message: 'Datei-Upload fehlgeschlagen.', clrAlertType: IAlertType.DANGER },
          4000
        );
      },
    });
  }

  deleteFromWarehouse(warehouseId: number): void {
    if (!confirm('Wollen Sie den Eintrag wirklich löschen?\nDiese Aktion kann nicht rückgängig gemacht werden!')) return;
    this.waress.deleteFromWarehouse(warehouseId).subscribe({
      next: () => {
        this.loadPositionsWarehouse();
        this.updateParent.emit();
        this.alertDispatcher.show(
          { message: 'Lagereintrag gelöscht.', clrAlertType: IAlertType.WARNING },
          3000
        );
      },
      error: () => {},
    });
  }

  showModal(fileId: string, description?: string): void {
    const token = getTenantStorage('access_token');
    this.modalUrl = `${this.filesDirectory}/download_file?file_id=${fileId}&token=${token}`;
    this.modalContent = description || 'Dokument';
    this.showPdf = true;
  }

  fileUrl(fileId: string): string {
    const token = getTenantStorage('access_token');
    return `${this.filesDirectory}/download_file?file_id=${fileId}&token=${token}`;
  }

  movementLabel(type: string): string {
    const labels: Record<string, string> = {
      incoming:          'Wareneingang',
      outgoing:          'Warenausgang',
      internal_transfer: 'Umbuchung',
      return_inbound:    'Rückgabe Ein',
      return_outbound:   'Rückgabe Aus',
      scrap:             'Ausschuss',
    };
    return labels[type] ?? type;
  }

  movementClass(type: string): string {
    switch (type) {
      case 'incoming':          return 'label label-success';
      case 'outgoing':          return 'label label-danger';
      case 'internal_transfer': return 'label label-info';
      case 'return_inbound':
      case 'return_outbound':   return 'label label-warning';
      case 'scrap':             return 'label';
      default:                  return 'label';
    }
  }

  pieceSign(type: string): string {
    return (type === 'outgoing' || type === 'return_outbound' || type === 'scrap') ? '−' : '+';
  }

  pieceClass(type: string): string {
    return (type === 'outgoing' || type === 'return_outbound' || type === 'scrap') ? 'pieces-out' : 'pieces-in';
  }
}
